import type { FamilyDraft } from '../api/coach-clients.js';

/**
 * The first box a family form still needs, for a button that is always
 * tappable (ruled 24 October for the whole app): a tap with something missing
 * puts the cursor in that box and says so under it, in the words the API's
 * refusals already use (family-problems.ts). Null when the form may be sent.
 *
 * The same rules as familyReady, in the order the parent's own page shows the
 * boxes: the parent, then each athlete.
 *
 * `input` is the box's id after the form's idPrefix (family-form.tsx).
 */
export function missingField(
  value: FamilyDraft,
  emailRequired: boolean,
): { field: string; message: string; input: string } | null {
  const empty = (text: string) => text.trim() === '';
  const required = (field: string, input: string) => ({ field, message: 'This is required.', input });

  if (empty(value.parent_first_name)) return required('parent_first_name', 'parent-first');
  if (empty(value.parent_last_name)) return required('parent_last_name', 'parent-last');

  const email = value.parent_email.trim();
  if (email === '' && emailRequired) return required('parent_email', 'parent-email');
  if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { field: 'parent_email', message: 'That is not an email address.', input: 'parent-email' };
  }

  if (value.parent_needs_birthday && empty(value.parent_date_of_birth ?? '')) {
    return required('parent_date_of_birth', 'parent-birthday');
  }

  for (const [index, athlete] of value.athletes.entries()) {
    if (empty(athlete.first_name)) return required(`athletes.${index}.first_name`, `athlete-${index}-first`);
    if (empty(athlete.last_name)) return required(`athletes.${index}.last_name`, `athlete-${index}-last`);
    if (athlete.needs_birthday && empty(athlete.date_of_birth ?? '')) {
      return required(`athletes.${index}.date_of_birth`, `athlete-${index}-birthday`);
    }
  }
  return null;
}
